import { AttributeComparisonSummaryProps } from "./types";
import { AttributeComparisonSummarySection } from "./AttributeComparisonSummarySection";
import { HorizontalBarChart } from "./charts/HorizontalBarChart";
import { useMemo } from "react";
import { useDemographicsColourAccessor } from "./charts/colorUtils";
import * as ATTRS from "./OutputAreaAttributesUS";
import { AttributeSummaryTable } from "./charts/AttributeSummaryTable";
import { hasAnyRequiredAttribute } from "./utils";

export function AttributeComparisonSummaryUS({
  outputArea,
  comparableDesignation,
  hiddenSections = [],
  printable = false,
}: AttributeComparisonSummaryProps) {
  const colorAccessor = useDemographicsColourAccessor(outputArea, comparableDesignation);

  const sections = useMemo(
    () => ({
      unemployment:
        hasAnyRequiredAttribute(ATTRS.UNEMPLOYMENT_ATTRS, outputArea) && !hiddenSections.includes("unemployment"),
      income: hasAnyRequiredAttribute(ATTRS.INCOME_ATTRS, outputArea) && !hiddenSections.includes("income"),
      population: hasAnyRequiredAttribute(ATTRS.POPULATION_ATTRS, outputArea) && !hiddenSections.includes("population"),
      age: hasAnyRequiredAttribute(ATTRS.AGE_ATTRS, outputArea) && !hiddenSections.includes("age"),
      housing: hasAnyRequiredAttribute(ATTRS.HOUSING_UNITS_ATTRS, outputArea) && !hiddenSections.includes("housing"),
      households: hasAnyRequiredAttribute(ATTRS.HOUSEHOLD_ATTRS, outputArea) && !hiddenSections.includes("households"),
    }),
    [outputArea, hiddenSections]
  );

  return (
    <>
      {sections.unemployment && (
        <AttributeComparisonSummarySection
          heading="Unemployment"
          designation={outputArea}
          comparableDesignation={comparableDesignation}
          headlineAttribute={ATTRS.UNEMPLOYMENT_ATTRS[0]}
          printable={printable}
        >
          <AttributeSummaryTable
            attributes={ATTRS.UNEMPLOYMENT_ATTRS}
            designation={outputArea}
            comparativeDesignation={comparableDesignation}
            colorAccessor={colorAccessor}
            printable={printable}
          />
        </AttributeComparisonSummarySection>
      )}
      {sections.income && (
        <AttributeComparisonSummarySection
          heading="Income"
          designation={outputArea}
          comparableDesignation={comparableDesignation}
          headlineAttribute={ATTRS.INCOME_ATTRS[0]}
          printable={printable}
        >
          <AttributeSummaryTable
            attributes={ATTRS.INCOME_ATTRS}
            designation={outputArea}
            comparativeDesignation={comparableDesignation}
            colorAccessor={colorAccessor}
            printable={printable}
          />
        </AttributeComparisonSummarySection>
      )}

      {sections.population && (
        <AttributeComparisonSummarySection
          heading="Population"
          designation={outputArea}
          comparableDesignation={comparableDesignation}
          headlineAttribute={ATTRS.POPULATION_ATTRS[0]}
          printable={printable}
        >
          <AttributeSummaryTable
            attributes={ATTRS.POPULATION_ATTRS}
            designation={outputArea}
            comparativeDesignation={comparableDesignation}
            colorAccessor={colorAccessor}
            printable={printable}
          />
        </AttributeComparisonSummarySection>
      )}

      {sections.age && (
        <AttributeComparisonSummarySection
          heading="Age"
          designation={outputArea}
          comparableDesignation={comparableDesignation}
          headlineAttribute={ATTRS.AGE_ATTRS[0]}
          printable={printable}
        >
          <AttributeSummaryTable
            attributes={ATTRS.AGE_ATTRS}
            designation={outputArea}
            comparativeDesignation={comparableDesignation}
            colorAccessor={colorAccessor}
            printable={printable}
          />
          <HorizontalBarChart
            designation={outputArea}
            comparativeDesignation={comparableDesignation}
            attributes={ATTRS.AGE_ATTRS.slice(1)}
            colorAccessor={colorAccessor}
            printable={printable}
          />
        </AttributeComparisonSummarySection>
      )}

      {sections.housing && (
        <AttributeComparisonSummarySection
          heading="Housing"
          designation={outputArea}
          comparableDesignation={comparableDesignation}
          headlineAttribute={ATTRS.HOUSING_UNITS_ATTRS[0]}
          printable={printable}
        >
          <AttributeSummaryTable
            attributes={ATTRS.HOUSING_UNITS_ATTRS}
            designation={outputArea}
            comparativeDesignation={comparableDesignation}
            colorAccessor={colorAccessor}
            printable={printable}
          />
        </AttributeComparisonSummarySection>
      )}

      {sections.households && (
        <AttributeComparisonSummarySection
          heading="Households"
          designation={outputArea}
          comparableDesignation={comparableDesignation}
          headlineAttribute={ATTRS.HOUSEHOLD_ATTRS[0]}
          printable={printable}
        >
          <AttributeSummaryTable
            attributes={ATTRS.HOUSEHOLD_ATTRS}
            designation={outputArea}
            comparativeDesignation={comparableDesignation}
            colorAccessor={colorAccessor}
            printable={printable}
          />
        </AttributeComparisonSummarySection>
      )}
    </>
  );
}
